/* Sparkline, porte de `spark()` (legacy/evolucao_classificador.html:721).
 *
 * Mantido a mao de proposito. Toda cor sai de `var(--...)`, entao trocar o tema
 * recolore o grafico sem re-render; uma biblioteca resolve cor em JS e exigiria
 * contexto de tema, key bump e um flash. Alem disso a matematica ja estava
 * escrita e medida — portar e transcricao, nao design. */

import { useId, useRef } from "react";
import { cell, compact, fmtMes, flowColor } from "../lib/format";

const W = 268, H = 74, PT = 10, PB = 16, PL = 2, PR = 2;

export interface SparklineProps {
  labels: string[];
  values: number[];
  /** Indice onde comecam os meses ainda nao realizados (agendados). */
  cutIndex: number;
  flow: string;
  title: string;
  onHover?: (info: { label: string; value: number; x: number; y: number } | null) => void;
  /** Altura em pixels. A proporção é mantida, então isto também alarga o desenho —
   *  é o que separa a sparkline do card do gráfico principal de uma página. */
  height?: number;
}

export function Sparkline({ labels, values, cutIndex, flow, title, onHover,
                            height = H }: SparklineProps) {
  const gradientId = useId();
  const svgRef = useRef<SVGSVGElement>(null);
  const n = values.length;
  if (!n) return null;

  const max = Math.max(...values, 0);
  const min = Math.min(...values, 0);
  const span = max - min || 1;
  const stepX = n > 1 ? (W - PL - PR) / (n - 1) : 0;
  const x = (i: number) => PL + i * stepX;
  const y = (v: number) => PT + (H - PT - PB) * (1 - (v - min) / span);

  const line = values.map((v, i) => `${i ? "L" : "M"}${x(i).toFixed(1)},${y(v).toFixed(1)}`).join("");
  const area = `${line}L${x(n - 1).toFixed(1)},${y(min).toFixed(1)}L${x(0).toFixed(1)},${y(min).toFixed(1)}Z`;
  const color = flowColor(flow);

  // Ultimo mes com valor dentro do realizado: onde o ponto final e desenhado.
  let lastRealized = -1;
  for (let i = Math.min(cutIndex, n) - 1; i >= 0; i--) {
    if (values[i]) { lastRealized = i; break; }
  }

  const handleMove = (event: React.PointerEvent<SVGSVGElement>) => {
    if (!onHover || !svgRef.current) return;
    const box = svgRef.current.getBoundingClientRect();
    const ratio = (event.clientX - box.left) / box.width;
    const i = Math.max(0, Math.min(n - 1, Math.round(ratio * (n - 1))));
    onHover({ label: labels[i], value: values[i], x: event.clientX, y: box.top });
  };

  // O viewBox escala uniformemente, entao um grafico mais alto agranda o rotulo junto.
  // Dividir pelo mesmo fator cancela isso e o texto sai sempre com ~9,5px na tela.
  const labelSize = 9.5 * (H / height);

  const realizedTotal = values.slice(0, cutIndex).reduce((a, b) => a + b, 0);
  const label = `${title}: total R$ ${compact(realizedTotal)} em ${cutIndex} meses realizados, ` +
    `máximo R$ ${compact(max)}${lastRealized >= 0 ? `, último em ${fmtMes(labels[lastRealized], true)}` : ""}`;

  return (
    <svg
      ref={svgRef}
      viewBox={`0 0 ${W} ${H}`}
      width="100%"
      height={height}
      role="img"
      aria-label={label}
      onPointerMove={handleMove}
      onPointerLeave={() => onHover?.(null)}
      style={{ display: "block", touchAction: "none" }}
    >
      <defs>
        <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity="0.26" />
          <stop offset="100%" stopColor={color} stopOpacity="0.02" />
        </linearGradient>
      </defs>

      {/* Meses agendados, ainda nao efetivados: sombreados, nunca somados como realizado. */}
      {cutIndex < n && (
        <rect x={x(Math.max(0, cutIndex - 0.5))} y={0}
              width={Math.max(0, W - x(Math.max(0, cutIndex - 0.5)))} height={H}
              fill="var(--grid)" opacity="0.5" />
      )}

      <line x1={PL} y1={y(0)} x2={W - PR} y2={y(0)}
            stroke="var(--axis)" strokeWidth="1" strokeDasharray="2 3" />
      <path d={area} fill={`url(#${gradientId})`} />
      <path d={line} fill="none" stroke={color} strokeWidth="1.6"
            strokeLinejoin="round" strokeLinecap="round" />

      {lastRealized >= 0 && (
        <circle cx={x(lastRealized)} cy={y(values[lastRealized])} r="2.6" fill={color} />
      )}

      <text x={PL} y={H - 4} fontSize={labelSize} fill="var(--ink-3)"
            fontFamily="var(--font-mono)">
        {fmtMes(labels[0])}
      </text>
      <text x={W - PR} y={H - 4} fontSize={labelSize} fill="var(--ink-3)"
            textAnchor="end" fontFamily="var(--font-mono)">
        {fmtMes(labels[n - 1])}
      </text>
      <text x={W - PR} y={10} fontSize={labelSize} fill="var(--ink-3)"
            textAnchor="end" fontFamily="var(--font-mono)">
        {cell(max)}
      </text>
    </svg>
  );
}
